// ─── Evaluation.jsx ─────────────────────────────────────────────────
import React from 'react';
import SEO from '../components/SEO';
import { PageHeader } from '../components/UI';
import { SCORE_DIMENSIONS } from '../data/content';
import styles from './InnerPage.module.css';

export function Evaluation() {
  const QA = [
    { q:'What is the document retention policy for financial records?', a:'Financial records must be retained for a minimum of 7 years per SOX compliance requirements.', conf:92, low:false },
    { q:'Who approves exception requests?', a:'Exception requests require approval from the Department Head and Compliance Officer.', conf:78, low:false },
    { q:'What happens when a document fails compliance review?', a:'Document does not contain sufficient information to answer this question.', conf:18, low:true },
  ];
  const TIERS = [
    { cls:'n', score:'≥ 90', name:'GenAI-Native',     desc:'Fully optimised for AI. Production-ready for RAG, copilots, and search.' },
    { cls:'r', score:'≥ 75', name:'GenAI-Ready',      desc:'Good quality with minor, addressable gaps. Suitable for most AI workflows.' },
    { cls:'i', score:'< 75', name:'Needs Improvement', desc:'Significant issues identified. Requires remediation before AI deployment.' },
    { cls:'u', score:'Unsafe',name:'Unsafe',           desc:'High risk. Do not use in AI pipelines without thorough human review.' },
  ];
  return (
    <>
      <SEO title="AI Document Evaluation Engine" description="ReadIQ scores every enterprise document across 12 quality dimensions to generate a 0–100 AI Friendliness Score. Identify gaps, hallucination risks, and compliance issues instantly." canonical="/evaluation" keywords="AI document evaluation, document quality score, GenAI readiness score, 12 dimension analysis"/>
      <PageHeader crumb="Evaluation" title={<>AI-Powered Document<br/>Evaluation Engine</>} desc="Scores every document across 12 quality dimensions to generate a precise 0–100 AI Friendliness Score — identifying exactly where gaps exist and how to fix them." statNum="12" statLbl="Quality Dimensions"/>

      <section className="section">
        <div className="wrap">
          <div className="eyebrow">Live Score View</div>
          <h2 className="sh">Document Score Breakdown</h2>
          <p className="sub">Each of the 12 dimensions scored individually — with gap flags and an overall weighted final score.</p>
          <div className={styles.evalLayout}>
            <div className={styles.evalSidebar}>
              <div className={styles.scoreBox}>
                <div className={styles.scoreNum}>84</div>
                <div className={styles.scoreLbl}>AI Friendliness Score</div>
                <div className={styles.scorePill}>GenAI-Ready</div>
              </div>
            </div>
            <div className={styles.evalMain}>
              <div className={styles.evalTitle}>Dimension Scores <span>All 12 · Evaluated 2 hours ago</span></div>
              <div className={styles.dimList}>
                {SCORE_DIMENSIONS.map(d => (
                  <div key={d.n} className={styles.dlRow}>
                    <div className={styles.dlN}>{d.n}</div>
                    <div className={styles.dlBody}>
                      <div className={styles.dlName}>{d.name}</div>
                      <div className={styles.dlTrack}><div className={styles.dlFill} style={{width:`${d.score}%`,background:d.color}}/></div>
                    </div>
                    <div className={styles.dlScore} style={{color:d.color}}>{d.score}</div>
                    <span className={styles.dlFlag} style={{background:d.flagBg,color:d.flagC}}>{d.flag}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="wrap">
          <div className="eyebrow">Interactive Intelligence</div>
          <h2 className="sh">The III Score — Information Intelligence Index</h2>
          <p className="sub">Ask questions directly on documents. AI responds with confidence scoring, weighted at 30% of the final score.</p>
          <div className={styles.iiiPanel}>
            <div>
              <div className={styles.iiiH3}>Scoring Formula</div>
              <p className={styles.iiiP}>The final score blends static evaluation with live Q&A performance — rewarding documents that genuinely answer questions well.</p>
              <div className={styles.scorePair}>
                {[['87','ReadIQ Score','#38bdf8'],['+',null,null],['78','III Score','var(--lime)'],['=',null,null],['84','Final','#fff']].map((x,i)=>
                  x[1] ? <div key={i} className={styles.spItem}><div className={styles.spNum} style={{color:x[2]}}>{x[0]}</div><div className={styles.spLbl}>{x[1]}</div></div>
                       : <div key={i} className={styles.spSep}>{x[0]}</div>
                )}
              </div>
            </div>
            <div className={styles.qaList}>
              {QA.map((q,i) => (
                <div key={i} className={styles.qaItem}>
                  <div className={styles.qaQ}>Q: {q.q}</div>
                  <div className={styles.qaA} style={q.low?{color:'rgba(255,255,255,.38)',fontStyle:'italic'}:{}}>{q.a}</div>
                  <div className={styles.qaConf}>
                    Confidence
                    <div className={styles.qaTrack}><div className={styles.qaFill} style={{width:`${q.conf}%`,background:q.low?'var(--red)':'var(--blue)'}}/></div>
                    {q.conf}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <div className="eyebrow">Score Classification</div>
          <h2 className="sh">Four tiers of document readiness</h2>
          <p className="sub">Every document classified based on its final weighted score.</p>
          <div className={styles.classCards}>
            {TIERS.map(t=>(
              <div key={t.cls} className={`${styles.ccard} ${styles[`ccard_${t.cls}`]}`}>
                <div className={styles.ccNum}>{t.score}</div>
                <div className={styles.ccName}>{t.name}</div>
                <div className={styles.ccDesc}>{t.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─── Capabilities.jsx ────────────────────────────────────────────────
export function Capabilities() {
  const RULES = [
    ['GDPR Data Classification','p'],['ISO 27001 — Section 8.2','p'],
    ['Information Security Policy v3','w'],['Data Retention Policy — Fin.','f'],
    ['Accessibility Standard AA','w'],['Brand Voice Guidelines','p'],
    ['SOX Financial Disclosure','p'],['HIPAA Safe Harbour','f'],
    ['Internal Nomenclature','p'],['Content Freshness Rule','w'],
  ];
  const { CAPABILITIES } = require('../data/content');
  const [reviewers,setReviewers] = React.useState(5);
  const [hours,setHours]         = React.useState(2);
  const [docs,setDocs]           = React.useState(200);
  const [rate,setRate]           = React.useState(75);
  const saved  = Math.round(reviewers*hours*docs*0.75);
  const monthly= (saved*rate).toLocaleString();
  const annual = (saved*rate*12).toLocaleString();
  return (
    <>
      <SEO title="Capabilities — Six Pillars of Document Intelligence" description="Explore ReadIQ's six core capabilities: AI evaluation, III Q&A scoring, draft generator, org-rule compliance, workflow governance, and analytics with ROI calculator." canonical="/capabilities" keywords="AI document capabilities, document draft generator, org rules compliance, document workflow, ROI calculator"/>
      <PageHeader crumb="Capabilities" title={<>Six Pillars of<br/>Document Intelligence</>} desc="Every capability purpose-built to bridge the gap between human-authored content and AI-ready knowledge assets — at enterprise scale." statNum="6" statLbl="Core Capabilities"/>

      <section className="section">
        <div className="wrap">
          <div className="eyebrow">Core Capabilities</div>
          <h2 className="sh">Everything you need to operationalise documents for AI</h2>
          <p className="sub">A complete platform that evaluates, improves, governs, and reports on every document in your enterprise library.</p>
          <div className={styles.capsGrid6}>
            {CAPABILITIES.map(c=>(
              <div key={c.num} className={styles.capCard}>
                <div className={styles.capNum}>{c.num}</div>
                <div className={styles.capIco}>
                   <img src={c.icon} width={24} height={24} />
                  </div>
                <h3>{c.title}</h3><p>{c.desc}</p>
                <ul className={styles.capList}>{c.bullets.map((b,i)=><li key={i}>{b}</li>)}</ul>
                <div className="tags">{c.tags.map((t,i)=><span key={i} className={`tag ${t.c||''}`}>{t.l}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="wrap">
          <div className="eyebrow">Deep Dive</div>
          <h2 className="sh">Organization Rules Compliance</h2>
          <p className="sub">Every document validated against your internal ruleset — with granular per-rule verdicts across 24 configurable rules.</p>
          <div className={styles.compGrid}>
            <div>
              <div className={styles.cgH3}>Rule-Level Scoring</div>
              <p className={styles.cgP}>Each rule is evaluated independently — giving precise, actionable verdicts rather than a single pass/fail result.</p>
              <div className={styles.ruleList}>
                {RULES.map(([name,v])=>(
                  <div key={name} className={styles.ruleRow}>
                    <span className={styles.ruleName}>{name}</span>
                    <span className={`${styles.rb} ${styles[`rb_${v}`]}`}>{v==='p'?'PASS':v==='w'?'PARTIAL':'FAIL'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className={styles.statBox}>
                <div className={styles.statBoxLabel}>Overall Compliance — 24 Rules</div>
                <div className={styles.statRow}>
                  {[['6','#4ade80','Pass'],['10','#fbbf24','Partial'],['8','#f87171','Fail']].map(([n,c,l])=>(
                    <div key={l}><div className={styles.statNum} style={{color:c}}>{n}</div><div className={styles.statLbl}>{l}</div></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <div className="eyebrow">Deep Dive</div>
          <h2 className="sh">AI Recommended Draft Generator</h2>
          <p className="sub">Side-by-side comparison of the original document and the AI-improved draft with inline change annotations.</p>
          <div className={styles.draftBox}>
            <div className={styles.draftHead}><h4>Data Retention Policy v2.1</h4><div className="tags"><span className="tag g">+14 Improvements</span><span className="tag a">3 Gaps Filled</span></div></div>
            <div className={styles.draftCols}>
              <div className={styles.dcol}>
                <div className={styles.dcolLbl}>Original</div>
                <div className={styles.dcolTxt}>
                  <p>Data should be kept for <span className={styles.dDel}>a reasonable amount of time</span> according to business needs. <span className={styles.dDel}>Managers are responsible</span> for ensuring compliance.</p>
                  <br/><p>Records may be deleted <span className={styles.dDel}>when no longer needed</span>. Exceptions by contacting IT.</p>
                  <br/><p style={{color:'var(--muted)',fontStyle:'italic'}}>[Section on financial records missing]</p>
                </div>
              </div>
              <div className={styles.dcol}>
                <div className={styles.dcolLbl}>AI Improved Draft</div>
                <div className={styles.dcolTxt}>
                  <p>Data must be retained for <span className={styles.dAdd}>a minimum period defined by regulatory requirements</span> (see Appendix A) <span className={styles.dNew}>and must not exceed the maximum retention period without written approval.</span></p>
                  <br/><p>Records may be deleted <span className={styles.dAdd}>only after the retention period has elapsed and approval from the Data Governance Committee has been obtained.</span></p>
                  <br/><p><span className={styles.dNew}>Financial Records: All financial records must be retained for a minimum of 7 years in accordance with SOX Section 802.</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="wrap">
          <div className="eyebrow">ROI Calculator</div>
          <h2 className="sh">Estimate your annual savings</h2>
          <p className="sub">Configure the model with your team's numbers — enterprise customers typically see annual savings.</p>
          <div className={styles.roiCalc}>
            <div className={styles.roiHead}><h3>💰 ROI Estimator</h3><p>Adjust the sliders — savings calculated at 75% effort reduction.</p></div>
            <div className={styles.roiBody}>
              <div className={styles.roiInputs}>
                {[['Document reviewers',1,50,reviewers,setReviewers,r=>`${r}`],['Hours per review',0.5,8,hours,setHours,v=>`${v}h`,0.5],['Documents / month',10,1000,docs,setDocs,v=>`${v}`,10],['Hourly rate (USD)',30,200,rate,setRate,v=>`$${v}`,5]].map(([label,min,max,val,setter,fmt,step=1])=>(
                  <div key={label} className={styles.roiField}>
                    <label>{label}</label>
                    <input type="range" min={min} max={max} step={step} value={val} onChange={e=>setter(+e.target.value)}/>
                    <div className={styles.roiVal}>{fmt(val)}</div>
                  </div>
                ))}
              </div>
              <div className={styles.roiOutput}>
                {[[`${saved.toLocaleString()}h`,'Hours saved per month'],[`$${monthly}`,'Cost saved per month'],[`$${annual}`,'Estimated annual savings',true]].map(([n,l,hi])=>(
                  <div key={l} className={`${styles.roiCard} ${hi?styles.roiHi:''}`}>
                    <div className={styles.roiNum}>{n}</div>
                    <div className={styles.roiLbl}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── Workflow.jsx ────────────────────────────────────────────────────
export function Workflow() {
  const DOCS = [
    ['Data Retention Policy v3.2','Updated 2h ago · Compliance','J. Reeves','91','#4ade80','Approved','ap'],
    ['Customer Onboarding SOP','Updated 5h ago · Operations','A. Chen','84','#7dd3fc','In Review','rv'],
    ['AI Usage Guidelines 2025','Submitted 1d ago · Legal','M. Patel','72','#fbbf24','Submitted','sb'],
    ['Product Knowledge Base — Q2','Draft saved 3h ago · Product','S. Williams','—','rgba(255,255,255,.25)','Draft','dr'],
    ['Security Incident Response Plan','Reviewed 1d ago · InfoSec','K. Okafor','61','#f87171','Rejected','rj'],
  ];
  const GOV = [
    { ico:'/images/icons/governance/approval-thresholds.svg', title:'Approval Thresholds', desc:'Configure minimum score thresholds required for documents to proceed through each lifecycle stage.' },
    { ico:'/images/icons/governance/full-audit-trail.svg', title:'Full Audit Trail', desc:'Every evaluation, comment, approval, and rejection is time-stamped and attributed to a named user.' },
    { ico:'/images/icons/governance/automated-notifications.svg', title:'Automated Notifications', desc:'Role-appropriate notifications at every stage transition — keeping stakeholders informed.' },
    { ico:'/images/icons/governance/backup-recovery.svg', title:'Backup & Recovery', desc:'Full database export/import and one-click reset — complete disaster recovery built in.' },
    { ico:'/images/icons/governance/data-management.svg', title:'Data Management', desc:'Orphan cleanup, Q&A reset, and evaluation history purge — keep your knowledge base clean.' },
    { ico:'/images/icons/governance/configurable-settings.svg', title:'Configurable Settings', desc:'Set minimum scores for auto-approval and tune LLM provider settings per environment.' },
  ];
  const RBAC = [
    ['🔑','Admin',     [1,1,1,1,1,1]],
    ['📊','Evaluator', [1,1,0,0,0,2]],
    ['👁️','Reviewer',  [0,0,1,0,0,1]],
    ['✅','Approver',  [0,0,1,1,0,1]],
    ['👤','Viewer',    [0,0,0,0,0,3]],
  ];
  const LIFECYCLE_STEPS = [
  { ico:'/images/icons/workflow/draft.svg',    n:'1', name:'Draft',    desc:'Author creates or uploads' },
  { ico:'/images/icons/workflow/submit.svg',   n:'2', name:'Submit',   desc:'Submitted for AI evaluation' },
  { ico:'/images/icons/workflow/evaluate.svg', n:'3', name:'Evaluate', desc:'AI scores across 12 dims',    isBlue:true },
  { ico:'/images/icons/workflow/review.svg',   n:'4', name:'Review',   desc:'Human reviewer inspects',     isAmber:true },
  { ico:'/images/icons/workflow/approve.svg',  n:'5', name:'Approve',  desc:'Published as AI-ready asset', isGreen:true },
];
  const HDRS = ['Upload/Draft','Evaluate','Review','Approve/Reject','Admin Settings','View Reports'];
  return (
    <>
      <SEO title="Document Workflow & Governance" description="ReadIQ provides end-to-end document lifecycle management with role-based access control, real-time tracking, immutable audit logs, and configurable approval thresholds." canonical="/workflow" keywords="document workflow, document governance, RBAC document platform, audit log, enterprise document lifecycle"/>
      <PageHeader crumb="Workflow" title={<>Governed Document<br/>Lifecycle Management</>} desc="Every document follows a structured, auditable path from first draft to approved AI-ready knowledge asset — with full role-based access control at every step." statNum="5" statLbl="Lifecycle Stages"/>

      <section className="section">
        <div className="wrap">
          <div className="eyebrow">Document Lifecycle</div>
          <h2 className="sh">From draft to AI-ready — every step governed</h2>
          <p className="sub">A clearly defined lifecycle ensures every document is evaluated, reviewed, and approved before entering any AI pipeline.</p>
          <div className={styles.lcGrid}>

            
            {LIFECYCLE_STEPS.map(({ ico, n, name, desc, isBlue, isAmber, isGreen }) => (
  <div key={n} className={styles.lcStep}>
    <div className={styles.lcBbl}>
      <img src={ico} alt={name} width={32} height={32} />
      <div className={styles.lcN} style={{ background: 'var(--blue)' }}>
        {n}
      </div>
    </div>
    <div className={styles.lcName}>{name}</div>
    <div className={styles.lcDesc}>{desc}</div>
  </div>
))}
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="wrap">
          <div className="eyebrow">Live Tracker</div>
          <h2 className="sh">Real-time visibility across the pipeline</h2>
          <p className="sub">Every document's lifecycle stage, owner, AI score, and approval status — in one unified view.</p>
          <div className={styles.tracker}>
            <div className={styles.tpHead}>📋 Document Queue <span>5 documents shown</span></div>
            {DOCS.map(([name,sub,owner,score,sc,badge,bc])=>(
              <div key={name} className={styles.tpRow}>
                <div><div className={styles.trName}>{name}</div><div className={styles.trSub}>{sub}</div></div>
                <div className={styles.trOwner}>👤 {owner}</div>
                <div className={styles.trScore} style={{color:sc}}>{score}</div>
                <span className={`${styles.trBadge} ${styles[`tb_${bc}`]}`}>{badge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* <section className="section">
        <div className="wrap">
          <div className="eyebrow">Role-Based Access Control</div>
          <h2 className="sh">Five roles. Precise permissions.</h2>
          <p className="sub">Every stakeholder has exactly the access they need — no more, no less.</p>
          <div className={styles.tableWrap}>
            <table className={styles.rbacTable}>
              <thead><tr><th>Role</th>{HDRS.map(h=><th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {RBAC.map(([ico,name,perms])=>(
                  <tr key={name}>
                    <td><div className={styles.roleCell}><span className={styles.roleIco}>{ico}</span><strong>{name}</strong></div></td>
                    {perms.map((p,i)=><td key={i}>{p===1?<span className={styles.py}>✓</span>:p===2?<span className={styles.pl}>Limited</span>:p===3?<span className={styles.pl}>Read-only</span>:<span className={styles.pn}>✗</span>}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section> */}

      <section className="section">
        <div className="wrap">
          <div className="eyebrow">Governance Features</div>
          <h2 className="sh">Built-in accountability at every step</h2>
          <p className="sub">Every action tracked, every decision logged, and every policy enforced automatically.</p>
          <div className={styles.govGrid}>
            {GOV.map(g=>(
              <div key={g.title} className={styles.govCard}>
                <div className={styles.govIco}>
                  <img src={g.ico} width={24} height={24} />
                  </div>
                <h3>{g.title}</h3><p>{g.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─── Analytics.jsx ───────────────────────────────────────────────────
export function Analytics() {
  const HM = [[85,'rgba(34,197,94,.2)','rgba(34,197,94,.3)','Structure & Org'],[82,'rgba(34,197,94,.16)','rgba(34,197,94,.25)','Clarity & Prec.'],[71,'rgba(245,158,11,.2)','rgba(245,158,11,.3)','Completeness'],[80,'rgba(34,197,94,.14)','rgba(34,197,94,.22)','Consistency'],[76,'rgba(10,142,200,.2)','rgba(10,142,200,.3)','Self-Sufficiency'],[73,'rgba(245,158,11,.16)','rgba(245,158,11,.25)','Q. Answerability'],[84,'rgba(34,197,94,.2)','rgba(34,197,94,.3)','Hallucination Risk'],[78,'rgba(10,142,200,.16)','rgba(10,142,200,.25)','Metadata'],[81,'rgba(34,197,94,.14)','rgba(34,197,94,.22)','Content Quality'],[69,'rgba(245,158,11,.2)','rgba(245,158,11,.3)','Content Richness'],[77,'rgba(10,142,200,.14)','rgba(10,142,200,.22)','Redundancy'],[61,'rgba(239,68,68,.2)','rgba(239,68,68,.3)','Reg. Compliance']];
  const EVALS = [
    ['Data Retention Policy v3.2','Compliance','93','88','91','var(--green-dk)','GenAI-Native','var(--green-lt)','2h ago'],
    ['Customer Onboarding SOP','Operations','86','80','84','var(--blue)','GenAI-Ready','var(--blue-lt)','5h ago'],
    ['AI Usage Guidelines 2025','Legal','74','69','72','var(--amber)','Needs Improvement','var(--amber-lt)','1d ago'],
    ['Security Incident Response Plan','InfoSec','58','67','61','var(--red)','Unsafe','var(--red-lt)','1d ago'],
  ];
  //const EXPORTS = [['📄','PDF Report','Formatted executive summary with score breakdowns and compliance status — board-ready.'],['📊','Excel Export','Full data export with all 12 dimension scores, classification, and III scores for BI analysis.'],['🗂️','Batch Evaluation','Upload and evaluate entire document libraries in a single ZIP operation.'],['📬','Scheduled Reports','Automated weekly or monthly digest reports delivered without manual intervention.']];
  const EXPORTS = [
  { ico:'/images/icons/analytics/pdf-report.svg',       title:'PDF Report',        desc:'Formatted executive summary with score breakdowns and compliance status — board-ready.' },
  { ico:'/images/icons/analytics/excel-export.svg',      title:'Excel Export',      desc:'Full data export with all 12 dimension scores, classification, and III scores for BI analysis.' },
  { ico:'/images/icons/analytics/batch-evaluation.svg',  title:'Batch Evaluation',  desc:'Upload and evaluate entire document libraries in a single ZIP operation.' },
  { ico:'/images/icons/analytics/scheduled-reports.svg', title:'Scheduled Reports', desc:'Automated weekly or monthly digest reports delivered without manual intervention.' },
];
  return (
    <>
      <SEO title="Analytics & Reporting Dashboard" description="ReadIQ analytics gives you live score trends, dimension heatmaps, quadrant score distribution, and board-ready PDF/Excel export across your document library." canonical="/analytics" keywords="document analytics, AI readiness reporting, document quality metrics, score distribution dashboard, GenAI readiness metrics"/>
      <PageHeader crumb="Analytics" title={<>Analytics &amp; Reporting<br/>Dashboard</>} desc="Track score trends, dimension performance, and quality distribution across your entire document library — with one-click PDF and Excel export." statNum="347" statLbl="Documents Evaluated"/>

      <section className="section">
        <div className="wrap">
          <div className="eyebrow">Live Dashboard</div>
          <h2 className="sh">Portfolio quality at a glance</h2>
          <p className="sub">Real-time insights across your entire document library — score trends, distribution, and dimension performance.</p>
          <div className={styles.dashGrid}>
            {[['78.4','Average ReadIQ Score','Across 347 documents','↑ +4.2 vs last month',true],['94','GenAI-Native Docs','Score ≥ 90 · 27% of library','↑ +12 this month',true],['38','Needs Attention','Score < 75 · requires review','↓ −7 from last month',false]].map(([n,l,s,t,up])=>(
              <div key={l} className={styles.dc}>
                <div className={styles.dcLbl}>{l}</div>
                <div className={styles.dcNum}>{n}</div>
                <div className={styles.dcSub}>{s}</div>
                <div className={`${styles.dcTrend} ${up?styles.up:styles.dn}`}>{t}</div>
              </div>
            ))}
            <div className={styles.dc} style={{gridColumn:'span 2'}}>
              <div className={styles.dcLbl}>Average Score Trend — Last 6 Months</div>
              <svg width="100%" height="120" viewBox="0 0 500 120" preserveAspectRatio="none" style={{marginTop:14}}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0A8EC8" stopOpacity=".15"/><stop offset="100%" stopColor="#0A8EC8" stopOpacity="0"/></linearGradient></defs>
                <line x1="0" y1="30" x2="500" y2="30" stroke="#e4e8f0" strokeWidth="1"/>
                <line x1="0" y1="60" x2="500" y2="60" stroke="#e4e8f0" strokeWidth="1"/>
                <line x1="0" y1="90" x2="500" y2="90" stroke="#e4e8f0" strokeWidth="1"/>
                <path d="M0,88 L100,80 L200,70 L300,62 L400,50 L500,38 L500,120 L0,120 Z" fill="url(#g)"/>
                <polyline points="0,88 100,80 200,70 300,62 400,50 500,38" fill="none" stroke="#0A8EC8" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>
                {[[0,88,'Oct'],[100,80,'Nov'],[200,70,'Dec'],[300,62,'Jan'],[400,50,'Feb'],[500,38,'Mar ▲ 78.4']].map(([x,y,m],i)=>(
                  <g key={m}><circle cx={x} cy={y} r={i===5?5:4} fill={i===5?'#0A8EC8':'#fff'} stroke="#0A8EC8" strokeWidth="2"/><text x={x} y="115" fontSize="11" fill={i===5?'#0A8EC8':'#9aa3b2'} textAnchor={i===5?'end':'middle'} fontFamily="Plus Jakarta Sans">{m}</text></g>
                ))}
              </svg>
            </div>
            <div className={styles.dc}>
              <div className={styles.dcLbl}>Quality Distribution</div>
              {[['GenAI-Native (≥90)','27%','var(--green)','94'],['GenAI-Ready (≥75)','42%','var(--blue)','146'],['Needs Improvement','20%','var(--amber)','69'],['Unsafe','11%','var(--red)','38']].map(([l,w,c,ct])=>(
                <div key={l} className={styles.distRow}>
                  <div className={styles.distLbl}>{l}</div>
                  <div className={styles.distTrack}><div className={styles.distFill} style={{width:w,background:c}}/></div>
                  <div className={styles.distCt}>{ct}</div>
                </div>
              ))}
            </div>
            <div className={styles.dc} style={{gridColumn:'span 3',background:'linear-gradient(135deg,var(--navy),var(--navy-mid))'}}>
              <div className={styles.dcLbl} style={{color:'rgba(255,255,255,.35)'}}>Dimension Performance — Portfolio Average</div>
              <div className={styles.hmGrid}>
                {HM.map(([v,bg,bd,l],i)=>(
                  <div key={l} className={styles.hmC} style={{background:bg,borderColor:bd}}>
                    <div className={styles.hmN}>{String(i+1).padStart(2,'0')}</div>
                    <div className={styles.hmV}>{v}</div>
                    <div className={styles.hmL}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.dc} style={{gridColumn:'span 3'}}>
              <div className={styles.dcLbl}>Recent Evaluations</div>
              <div style={{overflowX:'auto'}}>
                <table className={styles.rt}>
                  <thead><tr>{['Document','Category','DIQ','III','Final','Classification','Evaluated'].map(h=><th key={h}>{h}</th>)}</tr></thead>
                  <tbody>
                    {EVALS.map(([doc,cat,diq,iii,fin,c,cls,clsBg,when])=>(
                      <tr key={doc}>
                        <td><strong style={{color:'var(--navy)'}}>{doc}</strong></td>
                        <td><span className="tag">{cat}</span></td>
                        <td><strong style={{fontSize:17,fontWeight:800,color:c}}>{diq}</strong></td>
                        <td><strong style={{fontSize:17,fontWeight:800,color:c}}>{iii}</strong></td>
                        <td><strong style={{fontSize:17,fontWeight:800,color:c}}>{fin}</strong></td>
                        <td><span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:100,background:clsBg,color:c}}>{cls}</span></td>
                        <td style={{color:'var(--muted)'}}>{when}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="wrap">
          <div className="eyebrow">Export & Reports</div>
          <h2 className="sh">Share insights across your organisation</h2>
          <p className="sub">Generate professional reports in multiple formats — or evaluate entire document batches in one operation.</p>
          <div className={styles.exportGrid}>
            {EXPORTS.map(({ ico, title, desc }) => (
              <div key={title} className={styles.expCard}>
                <div className={styles.expIco}>
                  <img src={ico} alt={title} width={22} height={22} />
                </div>
                <div><h3>{title}</h3><p>{desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─── NotFound.jsx ────────────────────────────────────────────────────
export function NotFound() {
  return (
    <div style={{minHeight:'60vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',textAlign:'center',padding:'80px 20px'}}>
      <SEO title="404 — Page Not Found" description="The page you're looking for doesn't exist." canonical="/404"/>
      <div style={{fontSize:80,marginBottom:24}}>🔍</div>
      <h1 style={{fontSize:36,fontWeight:800,color:'var(--navy)',marginBottom:12}}>Page Not Found</h1>
      <p style={{fontSize:16,color:'var(--muted)',marginBottom:32}}>The page you're looking for doesn't exist or has been moved.</p>
      <a href="/" className="btn btn-navy" style={{padding:'12px 28px'}}>← Back to Home</a>
    </div>
  );
}
